import React, { useEffect, useMemo, useRef, useState } from 'react'
import { getStateName } from '../../../constants/shippingZones'
import type { CategoryItem } from '../../../../../gateways/breadstack/categoryApis'
import type { MailOrderShippingZone } from '../../../../../types/location'
import { buildCategoryLookups } from '../../../utils/categoryLookups'
import { getApiErrorMessage } from '../../../utils/getApiErrorMessage'
import { formatTaxPercent, zoneKey } from '../../../utils/shippingTax'
import { cloneMailOrderZone } from '../../../utils/zoneClone'
import { ShippingZoneDialog } from '../dialogs/ShippingZoneDialog'
import { DeleteZoneModal } from '../tabs/zones/DeleteZoneModal'
import {
  MapPinIcon,
  ShippingZoneRow,
  zoneTableHeaderRowStyle,
} from './ShippingZoneRow'

interface Props {
  zones: MailOrderShippingZone[]
  onChange: (next: MailOrderShippingZone[]) => Promise<void> | void
  saving: boolean
  categories: CategoryItem[]
  categoriesLoading: boolean
  /** Location-level shipping tax inherited by zones without an override. */
  defaultShippingTax: string
  /** Deep link: `${country}-${state}` of the zone to open for editing. */
  autoEditZoneKey?: string
  /** Bumped per deep-link click so the same zone can be re-opened. */
  autoEditVersion?: number
}

const headerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '16px 20px',
  borderBottom: '1px solid #E8ECF0',
}

const iconBadgeStyle: React.CSSProperties = {
  width: 36,
  height: 36,
  borderRadius: 10,
  background: '#DBEAFE',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
}

const titleStyle: React.CSSProperties = {
  fontSize: 14,
  fontWeight: 600,
  color: '#2C3E50',
  margin: 0,
}

const subtitleStyle: React.CSSProperties = {
  fontSize: 12,
  color: '#7F8C8D',
  margin: '2px 0 0',
  maxWidth: 520,
}

const badgeStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '2px 10px',
  fontSize: 11,
  fontWeight: 600,
  color: '#475569',
  background: '#F1F5F9',
  borderRadius: 999,
}

const exclusionsBadgeStyle: React.CSSProperties = {
  ...badgeStyle,
  color: '#B45309',
  background: '#FEF3C7',
}

const cardErrorStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  margin: '12px 20px 0',
  padding: '8px 12px',
  fontSize: 12,
  color: '#B91C1C',
  background: '#FEF2F2',
  border: '1px solid #FECACA',
  borderRadius: 8,
}

const ZONES_SAVE_FALLBACK = "Couldn't save shipping zones. Try again."

const addBtnStyle: React.CSSProperties = {
  padding: '8px 14px',
  fontSize: 13,
  fontWeight: 600,
  color: '#fff',
  background: '#2563EB',
  border: 'none',
  borderRadius: 8,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
}

const PlusIcon: React.FC<{ size?: number; color: string }> = ({
  size = 14,
  color,
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
)

export const ShippingZonesCard: React.FC<Props> = ({
  zones,
  onChange,
  saving,
  categories,
  categoriesLoading,
  defaultShippingTax,
  autoEditZoneKey,
  autoEditVersion = 0,
}) => {
  const cardRef = useRef<HTMLDivElement | null>(null)
  const [focusTaxField, setFocusTaxField] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null)
  const lookups = useMemo(() => buildCategoryLookups(categories), [categories])
  const [editIndex, setEditIndex] = useState<number | null>(null)
  // Cloned seed for the Duplicate flow (held in state so the dialog's
  // `initial` keeps a stable reference and the form isn't reset per render).
  const [duplicateSource, setDuplicateSource] =
    useState<MailOrderShippingZone | null>(null)
  const [pendingDeleteIndex, setPendingDeleteIndex] = useState<number | null>(null)
  const [confirmDeleteIndex, setConfirmDeleteIndex] = useState<number | null>(null)
  // Backend failure from the Add/Edit dialog (shown inside the dialog) vs. a
  // row-level remove-exclusion / delete-zone (shown inline in the card).
  const [saveError, setSaveError] = useState<string | null>(null)
  const [cardError, setCardError] = useState<string | null>(null)

  // `${country}-${state}` of every other zone — the backend rejects a second
  // zone for the same region with 400 "Duplicate mail order zone…".
  const takenRegions = useMemo(
    () =>
      zones
        .filter((_, i) => i !== editIndex)
        .map((z) => `${z.country}-${z.state}`),
    [zones, editIndex],
  )
  const totalExclusions = zones.reduce(
    (sum, z) => sum + (z.excluded_categories?.length ?? 0),
    0,
  )

  // Deep link from Overview → Shipping Tax: scroll here and open the zone's
  // editor with the tax input focused. Keyed on the version so repeated
  // clicks re-trigger; zones are deliberately read once per trigger.
  useEffect(() => {
    if (autoEditVersion <= 0) return
    cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    if (!autoEditZoneKey) return
    const index = zones.findIndex((z) => zoneKey(z) === autoEditZoneKey)
    if (index < 0) return
    setSaveError(null)
    setCardError(null)
    setEditIndex(index)
    setFocusTaxField(true)
    setDialogOpen(true)
  }, [autoEditVersion])

  const handleOpenAdd = () => {
    setFocusTaxField(false)
    setSaveError(null)
    setCardError(null)
    setEditIndex(null)
    setDuplicateSource(null)
    setDialogOpen(true)
  }

  const handleOpenEdit = (index: number) => {
    setFocusTaxField(false)
    setSaveError(null)
    setCardError(null)
    setEditIndex(index)
    setDuplicateSource(null)
    setDialogOpen(true)
  }

  // Duplicate = Add seeded from a copy of the zone (province cleared, rules
  // and exclusions kept). `takenRegions` includes the source because
  // `editIndex` is null, so re-picking its province is blocked.
  const handleOpenDuplicate = (index: number) => {
    const source = zones[index]
    if (!source) return
    setFocusTaxField(false)
    setSaveError(null)
    setCardError(null)
    setEditIndex(null)
    setDuplicateSource(
      cloneMailOrderZone(source, getStateName(source.country, source.state)),
    )
    setDialogOpen(true)
  }

  const handleClose = () => {
    if (saving) return
    setDialogOpen(false)
    setEditIndex(null)
    setDuplicateSource(null)
    setSaveError(null)
    setFocusTaxField(false)
  }

  const handleSave = async (zone: MailOrderShippingZone) => {
    const next =
      editIndex !== null
        ? zones.map((z, i) => (i === editIndex ? zone : z))
        : [...zones, zone]
    next.sort((a, b) => (a.state || '').localeCompare(b.state || ''))
    setSaveError(null)
    try {
      await onChange(next)
    } catch (err) {
      // Keep the dialog open with the user's edits so they can fix and retry.
      setSaveError(getApiErrorMessage(err, ZONES_SAVE_FALLBACK))
      return
    }
    setDialogOpen(false)
    setEditIndex(null)
    setDuplicateSource(null)
    setFocusTaxField(false)
    // Zones are tracked by position and re-sorted on save; collapse so the
    // expanded panel never jumps to a different zone.
    setExpandedIndex(null)
  }

  const handleRemoveExclusion = async (index: number, categoryId: number) => {
    if (saving) return
    setCardError(null)
    try {
      await onChange(
        zones.map((z, i) =>
          i === index
            ? {
                ...z,
                excluded_categories: (z.excluded_categories ?? []).filter(
                  (id) => id !== categoryId,
                ),
              }
            : z,
        ),
      )
    } catch (err) {
      setCardError(getApiErrorMessage(err, ZONES_SAVE_FALLBACK))
    }
  }

  const handleRequestDelete = (index: number) => {
    if (saving) return
    setConfirmDeleteIndex(index)
  }

  const handleCancelDelete = () => {
    if (pendingDeleteIndex !== null) return
    setConfirmDeleteIndex(null)
  }

  const handleConfirmDelete = async () => {
    if (confirmDeleteIndex === null) return
    const index = confirmDeleteIndex
    setPendingDeleteIndex(index)
    setCardError(null)
    try {
      const next = zones.filter((_, i) => i !== index)
      await onChange(next)
      setExpandedIndex(null)
    } catch (err) {
      setCardError(getApiErrorMessage(err, ZONES_SAVE_FALLBACK))
    } finally {
      // Close the confirm modal either way; a failure surfaces inline above
      // the zone table instead of behind the modal overlay.
      setConfirmDeleteIndex(null)
      setPendingDeleteIndex(null)
    }
  }

  const initialZone =
    editIndex !== null ? (zones[editIndex] ?? null) : duplicateSource
  const dialogMode =
    editIndex !== null ? 'edit' : duplicateSource ? 'duplicate' : 'add'
  const zoneCountLabel = `${zones.length} zone${zones.length !== 1 ? 's' : ''}`

  return (
    <>
      <div
        ref={cardRef}
        style={{
          scrollMarginTop: 16,
          background: '#fff',
          border: '1px solid #E8ECF0',
          borderRadius: 8,
          boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
          overflow: 'hidden',
        }}
      >
        <div style={headerStyle}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <div style={iconBadgeStyle}>
              <MapPinIcon />
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <h3 style={titleStyle}>Shipping Zones</h3>
                <span style={badgeStyle}>{zoneCountLabel}</span>
                {totalExclusions > 0 && (
                  <span style={exclusionsBadgeStyle}>
                    {totalExclusions} exclusion{totalExclusions !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
              <p style={subtitleStyle}>
                Define shipping areas and associated fees. Customers outside
                these zones will not be eligible for mail order at checkout.
                Zones without a custom rate use the location shipping tax (
                {formatTaxPercent(defaultShippingTax)}).
              </p>
            </div>
          </div>
          <button type="button" onClick={handleOpenAdd} style={addBtnStyle}>
            + Add Zone
          </button>
        </div>

        {cardError && (
          <div role="alert" style={cardErrorStyle}>
            <span style={{ flex: 1 }}>{cardError}</span>
            <button
              type="button"
              onClick={() => setCardError(null)}
              aria-label="Dismiss error"
              style={{
                border: 'none',
                background: 'transparent',
                color: '#B91C1C',
                fontSize: 16,
                lineHeight: 1,
                cursor: 'pointer',
                padding: 0,
              }}
            >
              ×
            </button>
          </div>
        )}

        {zones.length === 0 ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '48px 16px',
            }}
          >
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                background: '#EFF6FF',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 16,
              }}
            >
              <MapPinIcon size={24} color="#2563EB" />
            </div>
            <h4
              style={{
                fontSize: 13.5,
                fontWeight: 600,
                color: '#2C3E50',
                margin: '0 0 4px',
              }}
            >
              No shipping zones
            </h4>
            <p
              style={{
                fontSize: 12,
                color: '#7F8C8D',
                textAlign: 'center',
                margin: '0 0 16px',
                maxWidth: 320,
                lineHeight: 1.5,
              }}
            >
              Add shipping zones to define the regions where you offer mail
              order and their fee structure
            </p>
            <button
              type="button"
              onClick={handleOpenAdd}
              disabled={saving}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '6px 14px',
                fontSize: 12,
                fontWeight: 500,
                color: '#FFFFFF',
                background: '#2563EB',
                border: '1px solid #2563EB',
                borderRadius: 8,
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.55 : 1,
                fontFamily: 'inherit',
              }}
            >
              <PlusIcon size={14} color="#FFFFFF" />
              Add Zone
            </button>
          </div>
        ) : (
          <>
            <div style={zoneTableHeaderRowStyle}>
              <div>Zone Name</div>
              <div>Region</div>
              <div>Shipping Fee</div>
              <div>Min. Purchase</div>
              <div>Free Shipping At</div>
              <div>Shipping Tax</div>
              <div>Exclusions</div>
              <div />
            </div>
            {zones.map((zone, index) => (
              <ShippingZoneRow
                key={`${zone.country}-${zone.state}-${index}`}
                zone={zone}
                expanded={expandedIndex === index}
                saving={saving}
                isPendingDelete={pendingDeleteIndex === index}
                lookups={lookups}
                categoriesLoading={categoriesLoading}
                defaultShippingTax={defaultShippingTax}
                onToggleExpand={() =>
                  setExpandedIndex((prev) => (prev === index ? null : index))
                }
                onEdit={() => handleOpenEdit(index)}
                onDuplicate={() => handleOpenDuplicate(index)}
                onDelete={() => handleRequestDelete(index)}
                onRemoveExclusion={(id) => handleRemoveExclusion(index, id)}
              />
            ))}
          </>
        )}
      </div>

      <ShippingZoneDialog
        open={dialogOpen}
        mode={dialogMode}
        initial={initialZone}
        onClose={handleClose}
        onSave={handleSave}
        saving={saving}
        categories={categories}
        categoriesLoading={categoriesLoading}
        takenRegions={takenRegions}
        saveError={saveError}
        onClearSaveError={() => setSaveError(null)}
        defaultShippingTax={defaultShippingTax}
        autoFocusField={focusTaxField ? 'shipping_tax' : undefined}
      />

      <DeleteZoneModal
        open={confirmDeleteIndex !== null}
        zoneName={
          confirmDeleteIndex !== null
            ? zones[confirmDeleteIndex]?.name ||
              getStateName(
                zones[confirmDeleteIndex]?.country ?? '',
                zones[confirmDeleteIndex]?.state ?? '',
              )
            : undefined
        }
        saving={pendingDeleteIndex !== null}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
      />
    </>
  )
}
